import { createContext, useCallback, useContext, useState } from 'react'
import { resolveSharedLang, setLangPref } from '@weapps/design'
import { en, type Strings } from './en'
import { pt } from './pt'
import { de } from './de'

export type Lang = 'en' | 'pt' | 'de'
export const SUPPORTED_LANGS: Lang[] = ['en', 'pt', 'de']
const DICTS: Record<Lang, Strings> = { en, pt, de }

export function isLang(v: unknown): v is Lang {
  return typeof v === 'string' && (SUPPORTED_LANGS as string[]).includes(v)
}

// The Accounts App follows the shared Cross-App Preference (ADR 0098): the language override from the
// `.polux` cookie, else the browser language, fallback English.
function detectInitial(): Lang {
  return resolveSharedLang(SUPPORTED_LANGS, 'en')
}

interface LangCtx {
  lang: Lang
  t: Strings
  setLang: (l: Lang) => void
}
const Ctx = createContext<LangCtx | null>(null)

export function useLang(): LangCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error('useLang must be used within LanguageProvider')
  return c
}
export function useT(): Strings {
  return useLang().t
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => detectInitial())
  // Override writes the shared Cross-App Preference cookie, so it propagates to grow-mobile + store.
  const setLang = useCallback((l: Lang) => {
    setLangPref(l)
    setLangState(l)
  }, [])

  return <Ctx.Provider value={{ lang, t: DICTS[lang], setLang }}>{children}</Ctx.Provider>
}
